/**
 * Battle engine — pure logic, no Phaser (owner: combat-builder).
 *
 * Classic command turn-based flow: every round the actors are ordered by speed (party first on
 * ties); each `act()` resolves one command, appends the resulting events and moves the turn queue
 * forward, starting the next round or ending the bout when the queue is exhausted. All rolls come
 * from a SeededRng re-seeded from `seed + round`, so a replay of the same commands is identical.
 *
 * Tutorial guarantees (see sword-training.json `rules`):
 *   - enemy damage never drops a party member below 1 hp;
 *   - a party member at or below the low-hp threshold ends the bout with outcome 'downed'
 *     (the tutor stops; only the closing dialogue changes);
 *   - the bout ends with 'goal' once the goal is met and >= minRounds were fought, or with
 *     'rounds'/'goal' at maxRounds.
 * Script order: a player skill's `playerSkill` line fires after its hit resolves; a missed skill
 * fires `playerSkillMiss` (or the generic miss line) after the miss, so lines never interrupt a lunge.
 */
import { SeededRng } from '@/core/rng';
import type { BattleActorConfig, BattleConfig, BattleOutcome } from '@/core/types/contracts';
import { DEFAULT_RULES, type BattleActionSpec, type BattleRules, type BattleScriptEntry, type ScriptOn, type SwordTrainingConfig, type TutorAiConfig, type TutorMove } from './types';

export interface ActorState {
  id: string;
  name: string;
  side: 'party' | 'enemy';
  hp: number; maxHp: number;
  sp: number; maxSp: number;
  speed: number; attack: number; defense: number;
  actions: string[];
  ai: string | null;
  sprite: string;
  /** Halves incoming damage until this actor's next turn begins. */
  guarding: boolean;
  /** Already used the free 观察 this round. */
  observed: boolean;
}

export interface Intent { move: TutorMove; label: string; action: string; round: number }

export type BattleEvent =
  | { kind: 'start' }
  | { kind: 'round'; round: number; order: string[] }
  | { kind: 'turn'; actor: string; round: number }
  | { kind: 'action'; actor: string; action: string; target: string }
  | { kind: 'hit'; actor: string; action: string; target: string; damage: number; guarded: boolean; hp: number }
  | { kind: 'miss'; actor: string; action: string; target: string }
  | { kind: 'feint'; actor: string; action: string; target: string }
  | { kind: 'guard'; actor: string; sp: number; spDelta: number }
  | { kind: 'rest'; actor: string; target: string; spDelta: number; sp: number }
  | { kind: 'sp'; actor: string; delta: number; sp: number }
  | { kind: 'observe'; actor: string; target: string; intent: Intent | null; comment: string }
  | { kind: 'script'; on: ScriptOn; dialogue: string }
  | { kind: 'lowHp'; actor: string; hp: number }
  | { kind: 'invalid'; actor: string; action: string; reason: string }
  | { kind: 'end'; outcome: BattleOutcome; round: number };

export interface ActionView { action: BattleActionSpec; canUse: boolean; reason?: string }

export interface EngineSnapshot {
  round: number; turn: number; order: string[]; current: string | null; outcome: BattleOutcome | null;
  goalCount: number; actors: Record<string, { hp: number; sp: number; guarding: boolean }>; log: string[];
}

const ONCE_DEFAULT: Record<ScriptOn, boolean> = {
  start: true, round: false, playerGuard: true, playerSkill: true, playerSkillMiss: true, playerHit: true, playerMiss: true, playerLowHp: true, end: true,
};

function makeActor(c: BattleActorConfig, side: 'party' | 'enemy'): ActorState {
  return {
    id: c.id, name: c.name, side,
    hp: c.hp, maxHp: c.hp, sp: c.sp, maxSp: c.sp,
    speed: c.speed, attack: c.attack, defense: c.defense,
    actions: [...c.actions], ai: c.ai ?? null, sprite: c.sprite,
    guarding: false, observed: false,
  };
}

/** Accepts a plain BattleConfig too: rules/ai/sprites/dialogues default to empty. */
export function normaliseConfig(config: BattleConfig | SwordTrainingConfig): SwordTrainingConfig {
  const c = config as Partial<SwordTrainingConfig> & BattleConfig;
  return {
    ...c,
    title: c.title ?? c.id,
    actions: c.actions as BattleActionSpec[],
    script: c.script as BattleScriptEntry[],
    rules: { ...DEFAULT_RULES, ...(c.rules ?? {}) },
    ai: c.ai ?? [],
    dialogues: c.dialogues ?? [],
    sprites: c.sprites ?? {},
  };
}

export class BattleEngine {
  readonly config: SwordTrainingConfig;
  readonly rules: BattleRules;
  readonly actors = new Map<string, ActorState>();
  readonly actions = new Map<string, BattleActionSpec>();
  readonly ais = new Map<string, TutorAiConfig>();
  /** Revealed intents keyed by the observed actor id. */
  readonly intents = new Map<string, Intent>();
  readonly log: string[] = [];
  round = 0;
  turn = 0;
  order: string[] = [];
  outcome: BattleOutcome | null = null;
  goalCount = 0;
  private rng: SeededRng;
  private started = false;
  private readonly fired = new Set<string>();
  private readonly lowHpFloor = new Map<string, number>();

  constructor(config: BattleConfig | SwordTrainingConfig, readonly seed: number) {
    this.config = normaliseConfig(config);
    this.rules = this.config.rules;
    for (const p of this.config.party) this.actors.set(p.id, makeActor(p, 'party'));
    for (const e of this.config.enemies) this.actors.set(e.id, makeActor(e, 'enemy'));
    for (const a of this.config.actions) this.actions.set(a.id, a);
    for (const ai of this.config.ai) this.ais.set(ai.id, ai);
    for (const a of this.actors.values()) this.lowHpFloor.set(a.id, Math.floor(a.maxHp * this.rules.lowHpRatio));
    this.rng = new SeededRng(seed >>> 0);
  }

  // ------------------------------------------------------------------ queries

  actor(id: string): ActorState {
    const a = this.actors.get(id);
    if (!a) throw new Error(`[combat] unknown actor ${id}`);
    return a;
  }
  action(id: string): BattleActionSpec | undefined { return this.actions.get(id); }
  party(): ActorState[] { return [...this.actors.values()].filter((a) => a.side === 'party'); }
  enemies(): ActorState[] { return [...this.actors.values()].filter((a) => a.side === 'enemy'); }
  isEnded(): boolean { return this.outcome !== null; }
  current(): ActorState | null {
    if (this.outcome !== null || !this.started) return null;
    const id = this.order[this.turn];
    return id ? this.actor(id) : null;
  }
  /** Default target: first living actor of the other side. */
  defaultTarget(actorId: string): string {
    const me = this.actor(actorId);
    const other = [...this.actors.values()].find((a) => a.side !== me.side);
    return other ? other.id : actorId;
  }
  lowHpThreshold(actorId: string): number { return this.lowHpFloor.get(actorId) ?? 0; }
  goalMet(): boolean {
    const goal = this.config.end.goal;
    const need = this.config.end.goalCount ?? 1;
    if (goal === 'survive') return true;
    return this.goalCount >= need;
  }

  availableActions(actorId: string): ActionView[] {
    const a = this.actor(actorId);
    const out: ActionView[] = [];
    for (const id of a.actions) {
      const action = this.actions.get(id);
      if (!action) continue;
      let canUse = true; let reason: string | undefined;
      if (action.spCost > 0 && a.sp < action.spCost) { canUse = false; reason = '技力不足'; }
      if (action.kind === 'observe' && this.rules.observeIsFree && a.observed) { canUse = false; reason = '本回合已观察'; }
      out.push(reason ? { action, canUse, reason } : { action, canUse });
    }
    return out;
  }

  /** The scripted tutor move for a round (cycles the pattern). */
  moveForRound(actorId: string, round: number): { move: TutorMove; spec: TutorAiConfig['moves'][TutorMove] } | null {
    const a = this.actor(actorId);
    const ai = a.ai ? this.ais.get(a.ai) : undefined;
    if (!ai || ai.pattern.length === 0) return null;
    const move = ai.pattern[(Math.max(1, round) - 1) % ai.pattern.length] as TutorMove;
    return { move, spec: ai.moves[move] };
  }

  /** What `actorId` will do next (this round if it has not acted yet, otherwise next round). */
  peekIntent(actorId: string): Intent | null {
    const idx = this.order.indexOf(actorId);
    const acted = idx >= 0 && idx < this.turn;
    const round = this.started ? (acted ? this.round + 1 : this.round) : 1;
    const m = this.moveForRound(actorId, round);
    if (!m) return null;
    return { move: m.move, label: m.spec.label, action: m.spec.action, round };
  }

  /** AI command for the current round. Actors without AI fall back to their first usable action. */
  chooseAi(actorId: string): { action: string; target: string } {
    const target = this.defaultTarget(actorId);
    const m = this.moveForRound(actorId, this.round);
    if (m && this.actions.has(m.spec.action)) return { action: m.spec.action, target };
    const usable = this.availableActions(actorId).find((v) => v.canUse && v.action.kind !== 'observe');
    return { action: usable?.action.id ?? this.actor(actorId).actions[0] ?? 'attack', target };
  }

  snapshot(): EngineSnapshot {
    const actors: EngineSnapshot['actors'] = {};
    for (const a of this.actors.values()) actors[a.id] = { hp: a.hp, sp: a.sp, guarding: a.guarding };
    return { round: this.round, turn: this.turn, order: [...this.order], current: this.current()?.id ?? null, outcome: this.outcome, goalCount: this.goalCount, actors, log: [...this.log] };
  }

  // ------------------------------------------------------------------ flow

  start(): BattleEvent[] {
    if (this.started) return [];
    this.started = true;
    const ev: BattleEvent[] = [{ kind: 'start' }];
    this.pushScripts(ev, 'start');
    this.beginRound(ev);
    return this.record(ev);
  }

  /**
   * Resolve one command for the actor whose turn it is. Returns every event produced, including
   * the following turn/round/end transitions. Invalid commands produce a single 'invalid' event.
   */
  act(actorId: string, actionId: string, targetId?: string): BattleEvent[] {
    const ev: BattleEvent[] = [];
    const cur = this.current();
    if (!cur || cur.id !== actorId) { ev.push({ kind: 'invalid', actor: actorId, action: actionId, reason: '不是此人的回合' }); return this.record(ev); }
    const view = this.availableActions(actorId).find((v) => v.action.id === actionId);
    if (!view) { ev.push({ kind: 'invalid', actor: actorId, action: actionId, reason: '没有这个招式' }); return this.record(ev); }
    if (!view.canUse) { ev.push({ kind: 'invalid', actor: actorId, action: actionId, reason: view.reason ?? '不可用' }); return this.record(ev); }
    const action = view.action;
    const target = targetId ?? this.defaultTarget(actorId);
    const actor = cur;

    if (action.kind === 'observe') {
      actor.observed = true;
      const intent = this.peekIntent(target);
      const m = intent ? this.moveForRound(target, intent.round) : null;
      if (intent) this.intents.set(target, intent);
      ev.push({ kind: 'action', actor: actorId, action: actionId, target });
      ev.push({ kind: 'observe', actor: actorId, target, intent, comment: m?.spec.comment ?? '看不出什么。' });
      if (this.rules.observeIsFree) return this.record(ev);
      this.nextTurn(ev);
      return this.record(ev);
    }

    // SP cost / restore.
    if (action.spCost !== 0) {
      const before = actor.sp;
      actor.sp = clamp(actor.sp - action.spCost, 0, actor.maxSp);
      const delta = actor.sp - before;
      if (action.kind !== 'guard' && delta !== 0) ev.push({ kind: 'sp', actor: actorId, delta, sp: actor.sp });
    }
    ev.push({ kind: 'action', actor: actorId, action: actionId, target });
    if (actor.side === 'party' && action.kind === 'skill' && this.config.end.goal === 'useSkill') this.goalCount++;

    switch (action.kind) {
      case 'guard': {
        if (action.effect === 'rest') {
          const t = this.actor(target);
          const before = t.sp;
          t.sp = clamp(t.sp + (action.targetSp ?? 0), 0, t.maxSp);
          ev.push({ kind: 'rest', actor: actorId, target, spDelta: t.sp - before, sp: t.sp });
        } else {
          actor.guarding = true;
          ev.push({ kind: 'guard', actor: actorId, sp: actor.sp, spDelta: -action.spCost });
          if (actor.side === 'party') this.pushScripts(ev, 'playerGuard');
        }
        break;
      }
      case 'attack':
      case 'skill': {
        if (actor.side === 'enemy') this.intents.delete(actorId);
        if (action.effect === 'feint') { ev.push({ kind: 'feint', actor: actorId, action: actionId, target }); break; }
        const hit = this.rng.next() < action.hitChance;
        const t = this.actor(target);
        const playerSkill = actor.side === 'party' && action.kind === 'skill';
        if (!hit) {
          ev.push({ kind: 'miss', actor: actorId, action: actionId, target });
          // Scripts fire only once the thrust has resolved: a missed skill gets its own line when the
          // config has one (else the generic miss line, then the skill line so a plain BattleConfig
          // still hears its `playerSkill` hook).
          if (playerSkill) {
            if (this.pushScripts(ev, 'playerSkillMiss') === 0) { this.pushScripts(ev, 'playerMiss'); if (!this.hasScripts('playerSkillMiss')) this.pushScripts(ev, 'playerSkill'); }
          } else if (actor.side === 'party') this.pushScripts(ev, 'playerMiss');
          break;
        }
        const dmg = this.rollDamage(actor, t, action);
        t.hp = Math.max(0, t.hp - dmg);
        ev.push({ kind: 'hit', actor: actorId, action: actionId, target, damage: dmg, guarded: t.guarding, hp: t.hp });
        if (actor.side === 'party' && this.config.end.goal === 'landHits') this.goalCount++;
        if (playerSkill) this.pushScripts(ev, 'playerSkill'); // praise lands after the hit, never mid-lunge
        if (t.side === 'party') {
          this.pushScripts(ev, 'playerHit');
          if (t.hp <= this.lowHpThreshold(t.id)) {
            ev.push({ kind: 'lowHp', actor: t.id, hp: t.hp });
            this.pushScripts(ev, 'playerLowHp');
            this.end(ev, 'downed');
            return this.record(ev);
          }
        }
        break;
      }
    }
    this.nextTurn(ev);
    return this.record(ev);
  }

  // ------------------------------------------------------------------ internals

  private rollDamage(attacker: ActorState, target: ActorState, action: BattleActionSpec): number {
    const v = Math.max(0, Math.round(this.rules.variance));
    const variance = v > 0 ? this.rng.int(-v, v) : 0;
    let dmg = Math.round(attacker.attack * action.power - target.defense * 0.5 + variance);
    dmg = Math.max(1, dmg);
    if (target.guarding) dmg = Math.max(1, Math.ceil(dmg * this.rules.guardDamageMul));
    if (this.rules.enemiesNeverKill && attacker.side === 'enemy' && target.side === 'party') dmg = Math.max(0, Math.min(dmg, target.hp - 1));
    return dmg;
  }

  private beginRound(ev: BattleEvent[]): void {
    this.round++;
    this.rng = new SeededRng(SeededRng.hash(`${this.seed}:${this.round}`));
    const list = [...this.actors.values()];
    // Stable sort: speed desc, party before enemy on ties, then config order.
    this.order = list
      .map((a, i) => ({ a, i }))
      .sort((x, y) => (y.a.speed - x.a.speed) || ((x.a.side === 'party' ? 0 : 1) - (y.a.side === 'party' ? 0 : 1)) || (x.i - y.i))
      .map((x) => x.a.id);
    this.turn = 0;
    for (const a of this.actors.values()) a.observed = false;
    ev.push({ kind: 'round', round: this.round, order: [...this.order] });
    this.pushScripts(ev, 'round');
    this.enterTurn(ev);
  }

  private enterTurn(ev: BattleEvent[]): void {
    const id = this.order[this.turn];
    if (!id) return;
    const a = this.actor(id);
    a.guarding = false; // a guard lasts until the guarder's next turn
    ev.push({ kind: 'turn', actor: id, round: this.round });
  }

  private nextTurn(ev: BattleEvent[]): void {
    if (this.outcome !== null) return;
    this.turn++;
    if (this.turn < this.order.length) { this.enterTurn(ev); return; }
    // Round over: end conditions.
    const { maxRounds } = this.config.end;
    if (this.goalMet() && this.round >= this.rules.minRounds) { this.end(ev, 'goal'); return; }
    if (this.round >= maxRounds) { this.end(ev, this.goalMet() ? 'goal' : 'rounds'); return; }
    this.beginRound(ev);
  }

  private end(ev: BattleEvent[], outcome: BattleOutcome): void {
    if (this.outcome !== null) return;
    this.outcome = outcome;
    this.pushScripts(ev, 'end', outcome);
    ev.push({ kind: 'end', outcome, round: this.round });
  }

  /** True when the config declares at least one script for `on` (fired or not). */
  hasScripts(on: ScriptOn): boolean { return this.config.script.some((s) => s.on === on); }

  /** Appends the script events for `on`; returns how many fired (once-only entries that already fired count 0). */
  private pushScripts(ev: BattleEvent[], on: ScriptOn, outcome?: BattleOutcome): number {
    const entries = this.config.script.filter((s) => s.on === on);
    let picked: BattleScriptEntry[];
    if (on === 'round') {
      const specific = entries.filter((s) => s.round === this.round);
      picked = specific.length ? specific : entries.filter((s) => s.round === undefined);
    } else if (on === 'end') {
      const specific = entries.filter((s) => s.outcome === outcome);
      picked = specific.length ? specific : entries.filter((s) => s.outcome === undefined);
    } else picked = entries;
    let fired = 0;
    for (const s of picked) {
      const key = `${s.on}:${s.round ?? ''}:${s.outcome ?? ''}:${s.dialogue}`;
      const once = s.once ?? ONCE_DEFAULT[s.on];
      if (once && this.fired.has(key)) continue;
      this.fired.add(key);
      ev.push({ kind: 'script', on, dialogue: s.dialogue });
      fired++;
    }
    return fired;
  }

  private record(ev: BattleEvent[]): BattleEvent[] {
    for (const e of ev) { const line = this.describe(e); if (line) this.log.push(line); }
    return ev;
  }

  /** Chinese battle-log line for an event (null for silent events). */
  describe(e: BattleEvent): string | null {
    const n = (id: string) => this.actors.get(id)?.name ?? id;
    const an = (id: string) => this.actions.get(id)?.name ?? id;
    switch (e.kind) {
      case 'round': return `—— 第 ${e.round} 回合 ——`;
      case 'hit': return `${n(e.actor)}的「${an(e.action)}」命中${n(e.target)}，造成 ${e.damage} 点伤害${e.guarded ? '（防御减半）' : ''}。`;
      case 'miss': return `${n(e.actor)}的「${an(e.action)}」刺空了。`;
      case 'feint': return `${n(e.actor)}虚晃一剑，${n(e.target)}退了半步。`;
      case 'guard': return `${n(e.actor)}举剑防御${e.spDelta > 0 ? `，技力回复 ${e.spDelta}` : ''}。`;
      case 'rest': return `${n(e.actor)}收势而立，让${n(e.target)}喘了口气${e.spDelta > 0 ? `（技力 +${e.spDelta}）` : ''}。`;
      case 'observe': return e.intent ? `${n(e.actor)}凝神观察：${n(e.target)}的下一手是「${e.intent.label}」。` : `${n(e.actor)}凝神观察，却看不出什么。`;
      case 'sp': return e.delta < 0 ? `${n(e.actor)}消耗技力 ${-e.delta}。` : `${n(e.actor)}技力回复 ${e.delta}。`;
      case 'lowHp': return `${n(e.actor)}脚下一晃，站立不稳。`;
      case 'invalid': return `无法行动：${e.reason}`;
      case 'end': return e.outcome === 'goal' ? '切磋结束——招式学成。' : e.outcome === 'rounds' ? '切磋结束——日头偏西。' : '切磋中止——师父收剑。';
      default: return null;
    }
  }
}

function clamp(v: number, lo: number, hi: number): number { return Math.max(lo, Math.min(hi, v)); }

/** Replays a fixed policy for tests/tooling: party always picks `pick(engine, actorId)`; enemies use their AI. */
export function simulate(config: BattleConfig | SwordTrainingConfig, seed: number, pick: (engine: BattleEngine, actorId: string, round: number) => string, maxSteps = 200): { engine: BattleEngine; events: BattleEvent[] } {
  const engine = new BattleEngine(config, seed);
  const events: BattleEvent[] = [...engine.start()];
  let steps = 0;
  while (!engine.isEnded() && steps++ < maxSteps) {
    const cur = engine.current();
    if (!cur) break;
    if (cur.side === 'enemy') { const c = engine.chooseAi(cur.id); events.push(...engine.act(cur.id, c.action, c.target)); continue; }
    const id = pick(engine, cur.id, engine.round);
    const view = engine.availableActions(cur.id).find((v) => v.action.id === id && v.canUse);
    const chosen = view ? id : (engine.availableActions(cur.id).find((v) => v.canUse && v.action.kind !== 'observe')?.action.id ?? 'attack');
    events.push(...engine.act(cur.id, chosen));
  }
  return { engine, events };
}
